import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import PropTypes from "prop-types";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";

import Header from "components/common/Header";
import Loader from "components/common/Loader";
import FiltersHeaderLabel from "components/common/FiltersHeaderLabel";
import ProductCardLayout from "components/textTheme/layout/ProductCardLayout";
import SectionLabel from "components/textTheme/layout/SectionLabel";
import ViewMoreModal from "components/textTheme/layout/ViewMoreModal";

import filterAcrossProducts from "../assets/icons/filterAcross.png";
import GenerateTextThemes from "./../utils/apis/GenerateTextThemes";
import {
    setTextThemeIsLoading,
    setThemesByKey,
    updateThemesByKey,
    setThemeSelections,
    updateThemeSelection,
    setConsumerFilters,
    setFilterAcrossEnabled,
    saveFilterState,
} from "./../store/features/textThemeSlice";
import { showNotification } from "store/features/notificationSlice";
import { setPreserveModuleOnReturn } from "./../store/features/activeTabSlice";

// Text Theme Analysis is not performed for a sample of 10 or fewer respondents, in line with
// Unilever's AI ethics and data protection principles. Such products show a note instead of the
// analysis, and report no theme selection so that the analysis report export leaves them out too.
const TEXT_THEME_MIN_SAMPLE_SIZE = 10;

const isSampleSizeSuppressed = (sampleSize) => Number(sampleSize || 0) <= TEXT_THEME_MIN_SAMPLE_SIZE;

const INITIAL_TAB_STATE = {
    filterAcrossEnabled: false,
    filterSourceProductId: "",
    globalAppliedFilters: {},
    cardFilters: {},
    selectedDisplayLabels: {},
    chartModes: {},
};

const SOURCE_MODULE_TO_TAB_KEY = {
    "Agreement": "agreement",
    "Secondary Analysis": "secondary",
};

function getSectionMeta(backendVariable = "") {
    const normalizedBackendVariable = backendVariable.toUpperCase();

    if (normalizedBackendVariable.includes("ITM")) {
        return {
            sectionId: "itm",
            sectionLabel: "In The Moment",
        };
    }

    if (normalizedBackendVariable.includes("RECALL") || normalizedBackendVariable.startsWith("R_")) {
        return {
            sectionId: "recall",
            sectionLabel: "Recall",
        };
    }

    return {
        sectionId: "standard",
        sectionLabel: "Text Theme Analysis",
    };
}

function getSectionMetaFromResponse(section, backendVariable = "") {
    if (typeof section === "boolean") {
        return section
            ? { sectionId: "itm", sectionLabel: "In The Moment" }
            : { sectionId: "recall", sectionLabel: "Recall" };
    }

    const normalizedSection = String(section || "").toLowerCase();

    if (normalizedSection === "itm") {
        return {
            sectionId: "itm",
            sectionLabel: "In The Moment",
        };
    }

    if (normalizedSection === "recall") {
        return {
            sectionId: "recall",
            sectionLabel: "Recall",
        };
    }

    return getSectionMeta(backendVariable);
}

function getSelectionMeta(selectionKey = "") {
    const keyParts = selectionKey.split("-");
    const coreDataId = keyParts.shift() || "";
    const backendVariable = keyParts.pop() || "";
    const productCodeRaw = keyParts.join("-");

    return {
        selectionKey,
        coreDataId,
        productCode: (productCodeRaw || "").replace(/_/g, " "),
        backendVariable,
    };
}

function themesByKeyFromResult(result = []) {
    return result.reduce((acc, item) => {
        acc[item.selectionKey] = item.themes || [];
        return acc;
    }, {});
}

function getThemeRows(themeFrequency = []) {
    const themeRows = themeFrequency
        .filter((row) => String(row?.Theme || row?.theme || "").toUpperCase() !== "NA")
        .map((row) => ({
            name: row?.Theme || row?.theme || "",
            frequency: Number(row?.Count || row?.count || 0),
        }))
        .filter((row) => row.name);

    return themeRows.length ? themeRows : [{ name: "No Data", frequency: 0 }];
}

function getThemeCooccurrenceData(records = [], themeField = "theme") {
    const themeCounts = {};
    const pairCounts = {};

    records.forEach((record) => {
        const themes = [...new Set(
            String(record?.[themeField] || "")
                .split(",")
                .map((theme) => theme.trim())
                .filter((theme) => theme && theme.toUpperCase() !== "NA")
        )];

        themes.forEach((theme) => {
            themeCounts[theme] = (themeCounts[theme] || 0) + 1;
        });

        for (let firstIndex = 0; firstIndex < themes.length; firstIndex += 1) {
            for (let secondIndex = firstIndex + 1; secondIndex < themes.length; secondIndex += 1) {
                const [source, target] = [themes[firstIndex], themes[secondIndex]].sort();
                const pairKey = `${source}\u0000${target}`;
                pairCounts[pairKey] = (pairCounts[pairKey] || 0) + 1;
            }
        }
    });

    const maxMentions = Math.max(...Object.values(themeCounts), 1);
    const nodes = Object.entries(themeCounts).map(([name, value]) => ({
        id: name,
        name,
        value,
        symbolSize: 16 + (value / maxMentions) * 28,
    }));
    const links = Object.entries(pairCounts).map(([pairKey, value]) => {
        const [source, target] = pairKey.split("\u0000");
        return { source, target, value };
    });

    return { nodes, links };
}

function getSummaryText(insights = []) {
    if (!insights.length) {
        return "";
    }

    const firstInsight = insights[0] || {};
    return (
        firstInsight["Insight Summary"] ||
        firstInsight.insight_summary ||
        firstInsight.overall_insight_summary ||
        firstInsight.positive_insight_summary ||
        firstInsight.negative_insight_summary ||
        firstInsight.summary ||
        firstInsight.Insight ||
        firstInsight.insight ||
        ""
    );
}

function getAvailableFiltersFromStore(consumerFilterOptions = [], productCode = "", coreDataId = "") {
    const productFilters = consumerFilterOptions.find(
        (item) => item.product_code === productCode && item.core_data_id?.toString() === coreDataId?.toString()
    );
    const groupedVariables = productFilters?.consumer_filters?.grouped_variables || {};
    const filterMapping = productFilters?.consumer_filters?.backend_variable_mapping || {};

    return Object.entries(groupedVariables).reduce((acc, [displayLabel, backendVariables]) => {
        const filterBackendVariables = Array.isArray(backendVariables) ? backendVariables : [];
        const values = filterBackendVariables.flatMap((backendVariable) => filterMapping[backendVariable] || []);
        const uniqueValues = [...new Set(values)].filter(Boolean);

        if (uniqueValues.length) {
            acc.availableFilters[displayLabel] = uniqueValues;
            acc.filterBackendVariables[displayLabel] = filterBackendVariables;
        }

        return acc;
    }, { availableFilters: {}, filterBackendVariables: {} });
}

function getLocalConsumerFilter(product = {}, appliedFilters = {}) {
    return Object.entries(appliedFilters).reduce((acc, [displayLabel, values]) => {
        if (!values?.length) return acc;

        const backendVariables = product.filterBackendVariables?.[displayLabel] || [];
        backendVariables.forEach((backendVariable) => {
            acc[backendVariable] = values.join(",");
        });

        return acc;
    }, {});
}

function adaptThemeResult(responseData = {}, selectionKey = "", consumerFilterOptions = [], backendVariableMacroCategory = {}, displayLabelTranslation = {}, likertScaleData = {}) {
    const selection = getSelectionMeta(selectionKey);
    const rawThemes = responseData?.themes || [];
    const sectionMeta = getSectionMetaFromResponse(rawThemes[0]?.itm_toggle, selection.backendVariable);
    const backendVariables = responseData.backend_variable.split(",").filter(Boolean);
    const relatedBackendVariable = backendVariableMacroCategory[backendVariables[0]];
    const displayPrefix = `${relatedBackendVariable.replace(/_?OpenEnd$/i, "").replace(/_/g, " ")} Open End`;
    const variableDisplayLabel = displayLabelTranslation[relatedBackendVariable] || relatedBackendVariable;
    const scaleSize = likertScaleData[relatedBackendVariable];
    const variableLabel = scaleSize != null ? `${variableDisplayLabel} (${scaleSize})` : variableDisplayLabel;
    const { availableFilters, filterBackendVariables } = getAvailableFiltersFromStore(
        consumerFilterOptions,
        selection.productCode,
        selection.coreDataId
    );

    const createVerbatims = (optionThemeRecords, variableThemeRecords) => ({
        verbatimResponses: optionThemeRecords
            .filter((theme) => theme.response)
            .slice(0, 2)
            .map((theme) => ({
                quote: theme.response,
                ageRange: theme.age_bracket,
                gender: theme.gender,
                country: theme.country,
                sentiment: theme.sentiment,
            })),
        verbatimResponsesExpanded: optionThemeRecords.reduce((acc, theme) => {
            const ageGroup = theme.age_bracket || "Unknown";

            if (theme.response) {
                acc[ageGroup].push({
                    responseId: theme.response_id,
                    response: theme.response,
                    sentiment: theme.sentiment,
                });
            }
            return acc;
        }, variableThemeRecords.reduce((acc, theme) => {
            acc[theme.age_bracket || "Unknown"] = [];
            return acc;
        }, {})),
    });
    const filterByBackendVariable = (records = [], backendVariable) =>
        records.filter((record) => record.backend_variable === backendVariable);
    const createDisplayOption = (backendVariable, sentiment = null) => {
        const stream = sentiment || "overall";
        const themeField = sentiment ? `theme_${sentiment}` : "theme";
        const displayLabel = responseData.display_label[backendVariable];
        const openEndedQuestion = responseData.attribute_value[backendVariable];
        const variableThemeRecords = filterByBackendVariable(responseData.themes, backendVariable);
        const optionThemeRecords = sentiment
            ? variableThemeRecords.filter((theme) => theme[`theme_${sentiment}`])
            : variableThemeRecords;
        const { verbatimResponses, verbatimResponsesExpanded } =
            createVerbatims(optionThemeRecords, variableThemeRecords);

        return {
            id: sentiment ? `${backendVariable}-${sentiment}` : backendVariable,
            backendVariable,
            prefix: displayPrefix,
            label: sentiment
                ? `${displayLabel} (${sentiment[0].toUpperCase()}${sentiment.slice(1)} Sentiment)`
                : displayLabel,
            sentiment,
            themes: getThemeRows(filterByBackendVariable(
                responseData.theme_frequency?.[`${stream}_theme_frequency`],
                backendVariable
            )),
            webChartData: getThemeCooccurrenceData(optionThemeRecords, themeField),
            summary: getSummaryText(filterByBackendVariable(
                responseData.insights?.[`${stream}_insights`],
                backendVariable
            )),
            verbatimResponses,
            verbatimResponsesExpanded,
            openEndedQuestion,
        };
    };
    const displayLabels = backendVariables.flatMap((backendVariable) => {
        const isSentiment =
            responseData.type_of_analysis[backendVariable] ===
            "Theme Text Analysis + Sentiment Bifurcation";

        return isSentiment
            ? ["positive", "negative"].map((sentiment) => createDisplayOption(backendVariable, sentiment))
            : [createDisplayOption(backendVariable)];
    });
    const defaultDisplay = displayLabels[0];

    return {
        id: selection.selectionKey,
        sectionId: sectionMeta.sectionId,
        sectionLabel: sectionMeta.sectionLabel,
        productName: responseData?.product_title || selection.productCode,
        studyReference: responseData?.study_reference_title || responseData?.study_reference || "",
        sampleSize: Number(responseData?.sample_size || 0),
        displayLabels,
        themes: defaultDisplay.themes,
        verbatimResponses: defaultDisplay.verbatimResponses,
        verbatimResponsesExpanded: defaultDisplay.verbatimResponsesExpanded,
        openEndedQuestion: defaultDisplay.openEndedQuestion,
        summary: defaultDisplay.summary,
        availableFilters,
        filterBackendVariables,
        variableId: variableLabel,
        variableLabel,
        productCode: selection.productCode,
        coreDataId: selection.coreDataId,
        backendVariables: [...new Set(rawThemes.map((theme) => theme.backend_variable).filter(Boolean))],
        isNoData: Boolean(
            responseData?.is_no_data ?? responseData?.meta?.is_no_data
        ),
    };
}

function attachAvailableFilters(product = {}, consumerFilterOptions = []) {
    const { availableFilters, filterBackendVariables } = getAvailableFiltersFromStore(
        consumerFilterOptions,
        product.productCode,
        product.coreDataId
    );

    return {
        ...product,
        availableFilters,
        filterBackendVariables,
    };
}

function getProductsByVariable(products = []) {
    return products.reduce((acc, product) => {
        if (!acc[product.variableId]) acc[product.variableId] = [];
        acc[product.variableId].push(product);
        return acc;
    }, {});
}

function getVariableTabs(products = []) {
    return [...new Map(products.map((product) => [
        product.variableId,
        { id: product.variableId, label: product.variableLabel },
    ])).values()];
}

function themeSelectionsFromOption(product, option) {
    const sentiment = option?.sentiment || null;
    // Only the backend variable behind the display label selected in the card's
    // dropdown is visible in the UI, so that is the only one we report.
    const backendVariable = option?.backendVariable;
    if (!backendVariable) return [];
    // The card shows the small sample note instead of the analysis, so the product reports no
    // selection at all - that is what keeps its themes out of the analysis report export.
    if (isSampleSizeSuppressed(product?.sampleSize)) return [];

    return [{
        product_code: product.productCode,
        core_data_id: product.coreDataId,
        backend_variable: backendVariable,
        sentiment,
    }];
}

function buildDefaultThemeSelections(products = []) {
    return products.reduce((acc, product) => {
        acc[product.id] = themeSelectionsFromOption(product, product.displayLabels?.[0]);
        return acc;
    }, {});
}

function getSectionsFromProducts(products = []) {
    const sectionOrder = ["itm", "recall", "standard"];
    const sectionMap = products.reduce((acc, product) => {
        const sectionId = product.sectionId || "standard";
        if (!acc[sectionId]) {
            acc[sectionId] = {
                id: sectionId,
                label: product.sectionLabel || "Text Theme Analysis",
            };
        }
        return acc;
    }, {});

    return sectionOrder.filter((sectionId) => sectionMap[sectionId]).map((sectionId) => sectionMap[sectionId]);
}

function hasAnyFilter(filters) {
    return Object.values(filters || {}).some((values) => values?.length);
}

function getFilterStatus(product, globalFilters, filterSourceProductId = "") {
    if (!hasAnyFilter(globalFilters)) return null;

    if (product.id === filterSourceProductId) {
        return { applied: globalFilters, missing: {} };
    }

    const applied = {};
    const missing = {};

    Object.entries(globalFilters).forEach(([category, values]) => {
        const productOptions = product.availableFilters[category] || [];
        const presentValues = values.filter((value) => productOptions.includes(value));
        const missingValues = values.filter((value) => !productOptions.includes(value));

        if (presentValues.length) applied[category] = presentValues;
        if (missingValues.length) missing[category] = missingValues;
    });

    if (product.filteredNoData && !hasAnyFilter(missing)) {
        const lastAppliedCategory = Object.keys(applied).pop();

        if (lastAppliedCategory) {
            missing[lastAppliedCategory] = applied[lastAppliedCategory];
            delete applied[lastAppliedCategory];
        }
    }

    return { applied, missing };
}

function chunkProducts(products, size = 2) {
    const rows = [];
    for (let index = 0; index < products.length; index += size) {
        rows.push(products.slice(index, index + size));
    }
    return rows;
}

function TextThemeAnalysis({ headerTitle = "Theme Text Analysis" }) {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { selections, isLoading } = useSelector((state) => state.textThemeStore);
    // Read persisted state from Redux
    const persistedRowStates = useSelector((state) => state.textThemeStore.rowStates);
    const persistedLocalConsumerFilters = useSelector((state) => state.textThemeStore.localConsumerFilters);
    const persistedProductsByVariable = useSelector((state) => state.textThemeStore.productsByVariable);
    const persistedVariableTabs = useSelector((state) => state.textThemeStore.variableTabs);
    const persistedActiveVariableId = useSelector((state) => state.textThemeStore.activeVariableId);
    const productCategory = useSelector((state) => state.settingsStore.productCategory);
    // const consumerFilter = useSelector((state) => state.consumerFilterStore.currentSelectedFilters);
    // const consumerFilter = {};
    const consumerFilterOptions = useSelector((state) => state.consumerFilterStore.filters);
    const backendVariableMacroCategory = useSelector((state) => state.dataMappingStore.backendVariableMacroCategory);
    const displayLabelTranslation = useSelector((state) => state.dataMappingStore.displayLabelTranslation);
    const likertScaleData = useSelector((state) => state.analysisResultsStore.likertScaleData);
    const [modalData, setModalData] = useState(null);
    // Seed local state from Redux on mount — restores everything when returning via Back from Respondent Module
    const [rowStates, setRowStates] = useState(() => persistedRowStates);
    const [localConsumerFilters, setLocalConsumerFilters] = useState(() => persistedLocalConsumerFilters);
    const [variableTabs, setVariableTabs] = useState(() => persistedVariableTabs);
    const [activeVariableId, setActiveVariableId] = useState(() => persistedActiveVariableId || "");
    const [productsByVariable, setProductsByVariable] = useState(() => persistedProductsByVariable);
    const [loadError, setLoadError] = useState("");
    const consumerFilterOptionsRef = useRef(consumerFilterOptions);

    useEffect(() => {
        consumerFilterOptionsRef.current = consumerFilterOptions;
    }, [consumerFilterOptions]);

    const mergeUpdatedProducts = (updatedProducts = [], noDataSelectionKeys = []) => {
        setProductsByVariable((currentProductsByVariable) => Object.entries(currentProductsByVariable).reduce((acc, [variableId, products]) => {
            acc[variableId] = products.map((product) => {
                const updatedProduct = updatedProducts.find((item) => item.id === product.id);
                if (updatedProduct) return { ...updatedProduct, filteredNoData: false };
                return noDataSelectionKeys.includes(product.id) ? { ...product, filteredNoData: true } : product;
            });
            return acc;
        }, {}));
    };

    // A consumer filter can drop a product to 10 or fewer respondents - or lift it back above the
    // threshold - so the theme selections are re-derived from the refreshed sample sizes.
    const syncThemeSelections = (refreshedProducts = []) => {
        refreshedProducts.forEach((product) => {
            const sectionState = rowStates[`${activeVariableId}-${product.sectionId}`] || INITIAL_TAB_STATE;
            const selectedDisplayId = sectionState.selectedDisplayLabels?.[product.id];
            const selectedOption = product.displayLabels.find((option) => option.id === selectedDisplayId)
                || product.displayLabels[0];

            dispatch(updateThemeSelection({
                productId: product.id,
                selections: themeSelectionsFromOption(product, selectedOption),
            }));
        });
    };

    const refreshFilteredProducts = async (selectionKeys, nextLocalConsumerFilters) => {
        if (!selectionKeys.length) return;

        dispatch(setTextThemeIsLoading(true));
        try {
            const consumerFilter = {};
            const result = await GenerateTextThemes({
                selections,
                productCategory,
                consumerFilter,
                localConsumerFilters: nextLocalConsumerFilters,
                selectionKeys,
                backendVariableMacroCategory,
            });
            const availableResults = result.filter((responseData) => !responseData.is_no_data);
            const noDataSelectionKeys = result
                .filter((responseData) => responseData.is_no_data)
                .map((responseData) => responseData.selectionKey);

            if (availableResults.length) {
                dispatch(updateThemesByKey(themesByKeyFromResult(availableResults)));
            }
            const updatedProducts = availableResults.map((responseData) => adaptThemeResult(
                responseData,
                responseData.selectionKey,
                consumerFilterOptions,
                backendVariableMacroCategory,
                displayLabelTranslation,
                likertScaleData
            ));
            mergeUpdatedProducts(updatedProducts, noDataSelectionKeys);
            syncThemeSelections(updatedProducts);
        } catch (error) {
            dispatch(
                showNotification({
                    severity: "error",
                    data: error?.message || "Failed to generate filtered text themes.",
                })
            );
        } finally {
            dispatch(setTextThemeIsLoading(false));
        }
    };

    useEffect(() => {
        const activeSelections = selections.filter(item => item.isSelected);

        if (activeSelections.length === 0) {
            navigate(-1);
            return;
        }

        const mappingsReady = activeSelections.every(
            selection => selection.backendVariable
                .split(",")
                .every(backendVariable => backendVariableMacroCategory[backendVariable])
        );

        if (!mappingsReady) return;

        // If products are already in Redux skip re-fetching entirely
        if (Object.keys(persistedProductsByVariable).length > 0) return;

        const fetchThemes = async () => {
            dispatch(setTextThemeIsLoading(true));
            setLoadError("");

            try {
                const consumerFilter = {};
                const result = await GenerateTextThemes({
                    selections: activeSelections,
                    productCategory,
                    consumerFilter,
                    backendVariableMacroCategory,
                });
                dispatch(setThemesByKey(themesByKeyFromResult(result)));
                const products = result.map((responseData) => adaptThemeResult(
                    responseData,
                    responseData.selectionKey,
                    consumerFilterOptionsRef.current,
                    backendVariableMacroCategory,
                    displayLabelTranslation,
                    likertScaleData
                ));
                dispatch(setThemeSelections(buildDefaultThemeSelections(products)));
                const nextVariableTabs = getVariableTabs(products);
                setVariableTabs(nextVariableTabs);
                setActiveVariableId(nextVariableTabs[0]?.id || "");
                setProductsByVariable(getProductsByVariable(products));
            } catch (error) {
                setLoadError("Unable to load Theme Text Analysis results.");
                setVariableTabs([]);
                setActiveVariableId("");
                setProductsByVariable({});
                dispatch(
                    showNotification({
                        severity: "error",
                        data: error?.message || "Failed to generate text themes.",
                    })
                );
            } finally {
                dispatch(setTextThemeIsLoading(false));
            }
        };

        fetchThemes();
    }, [selections, productCategory, backendVariableMacroCategory, displayLabelTranslation, likertScaleData, dispatch, navigate, persistedProductsByVariable]);

    // Sync state to Redux whenever it changes so it survives navigation to Respondent Module
    useEffect(() => {
        if (!Object.keys(productsByVariable).length) return;
        dispatch(saveFilterState({
            rowStates,
            localConsumerFilters,
            productsByVariable,
            variableTabs,
            activeVariableId,
        }));
    }, [productsByVariable, rowStates, localConsumerFilters, variableTabs, activeVariableId, dispatch]);

    useEffect(() => {
        if (!consumerFilterOptions?.length) return;

        setProductsByVariable((currentProductsByVariable) => Object.entries(currentProductsByVariable).reduce((acc, [variableId, products]) => {
            acc[variableId] = products.map((product) => attachAvailableFilters(product, consumerFilterOptions));
            return acc;
        }, {}));
    }, [consumerFilterOptions]);

    const activeProducts = productsByVariable[activeVariableId] || [];
    const activeSections = getSectionsFromProducts(activeProducts);
    const getProductsForSection = (sectionId) => activeProducts.filter((product) => product.sectionId === sectionId);
    const showSectionLabels = location.state?.from === "Overall Analysis";

    useEffect(() => {
        const products = productsByVariable[activeVariableId] || [];
        const applied = [];
        const missing = [];
        let filterAcrossEnabled = false;

        getSectionsFromProducts(products).forEach((section) => {
            const sectionProducts = products.filter((product) => product.sectionId === section.id);
            const sectionKey = `${activeVariableId}-${section.id}`;
            const sectionState = rowStates[sectionKey] || INITIAL_TAB_STATE;

            if (sectionState.filterAcrossEnabled) {
                filterAcrossEnabled = true;
            }

            sectionProducts.forEach((product) => {
                const base = {
                    product_code: product.productCode,
                    core_data_model_id: String(product.coreDataId),
                    backend_variable: (product.backendVariables || []).join(","),
                };
                const filterStatus = sectionState.filterAcrossEnabled
                    ? getFilterStatus(
                        product,
                        sectionState.globalAppliedFilters,
                        sectionState.filterSourceProductId
                    )
                    : null;
                const appliedFilters = sectionState.filterAcrossEnabled
                    ? filterStatus?.applied || {}
                    : sectionState.cardFilters[product.id] || {};
                const missingFilters = filterStatus?.missing || {};

                const appliedPayload = getLocalConsumerFilter(product, appliedFilters);
                const missingPayload = getLocalConsumerFilter(product, missingFilters);

                if (Object.keys(appliedPayload).length) {
                    applied.push({ ...base, ...appliedPayload });
                }
                if (Object.keys(missingPayload).length) {
                    missing.push({ ...base, ...missingPayload });
                }
            });
        });

        dispatch(setConsumerFilters({ applied, missing }));
        dispatch(setFilterAcrossEnabled(filterAcrossEnabled));
    }, [activeVariableId, productsByVariable, rowStates, dispatch]);

    const getRowState = (rowKey) => rowStates[rowKey] || INITIAL_TAB_STATE;

    const updateRowState = (rowKey, patch) => {
        const currentRowState = getRowState(rowKey);
        const nextRowStates = {
            ...rowStates,
            [rowKey]: {
                ...currentRowState,
                ...patch,
            },
        };
        setRowStates(nextRowStates);
        // Persist to Redux so it survives navigation away and back
        dispatch(saveFilterState({ rowStates: nextRowStates, localConsumerFilters }));
    };

    const getAppliedFilters = (product, filters, filterSourceProductId) =>
        getFilterStatus(product, filters, filterSourceProductId)?.applied || {};

    const handleCardFilterChange = (rowKey, rowProducts, productId, nextFilters) => {
        const currentRowState = getRowState(rowKey);
        const nextCardFilters = currentRowState.filterAcrossEnabled
            ? rowProducts.reduce((acc, product) => ({ ...acc, [product.id]: nextFilters }), {})
            : { ...currentRowState.cardFilters, [productId]: nextFilters };
        const nextLocalConsumerFilters = currentRowState.filterAcrossEnabled
            ? rowProducts.reduce((acc, product) => ({
                ...acc,
                [product.id]: getLocalConsumerFilter(
                    product,
                    getAppliedFilters(product, nextFilters, productId)
                ),
            }), {})
            : { [productId]: getLocalConsumerFilter(rowProducts.find((product) => product.id === productId), nextFilters) };
        const selectionKeysToRefresh = currentRowState.filterAcrossEnabled ? rowProducts.map((product) => product.id) : [productId];

        updateRowState(rowKey, {
            cardFilters: nextCardFilters,
            globalAppliedFilters: currentRowState.filterAcrossEnabled ? nextFilters : currentRowState.globalAppliedFilters,
            filterSourceProductId: currentRowState.filterAcrossEnabled
                ? productId
                : currentRowState.filterSourceProductId,
        });
        const mergedFilters = {
            ...localConsumerFilters,
            ...nextLocalConsumerFilters,
        };

        setLocalConsumerFilters(mergedFilters);
        // Persist to Redux so it survives navigation away and back
        dispatch(saveFilterState({ rowStates, localConsumerFilters: mergedFilters }));
        refreshFilteredProducts(selectionKeysToRefresh, mergedFilters);
    };

    const handleFilterAcrossToggle = (rowKey) => {
        const currentRowState = getRowState(rowKey);
        const nextEnabled = !currentRowState.filterAcrossEnabled;

        updateRowState(rowKey, {
            filterAcrossEnabled: nextEnabled,
            globalAppliedFilters: {},
            filterSourceProductId: "",
        });
    };

    const handleBackClick = () => {
        const moduleKey = SOURCE_MODULE_TO_TAB_KEY[location.state?.from];
        if (moduleKey) {
            dispatch(setPreserveModuleOnReturn(moduleKey));
        }
        navigate(-1);
    };

    return (
        <>
            {isLoading && <Loader />}
            <Header
                title={headerTitle}
                titleSuffix="(Powered by ChatGPT 5)"
                isStudySelected={true}
            />
            <div
                className="ms-4 me-4 mb-0 mt-3 background-color-white box-shadow--2 d-flex"
                style={{
                    borderRadius: "5px",
                }}
            >
                <FiltersHeaderLabel multipleLabel={true} />
            </div>
            <div className="tta-page full-container m-4 background-color-white box-shadow--full-container">
                <button
                    type="button"
                    className="tta-back p-2 liner-light-blue-background color-white"
                    onClick={handleBackClick}
                    aria-label="Back"
                >
                    <ArrowBackIcon className="color-white" />
                </button>
                <main className="tta-main-content">
                    {loadError && <p className="text-xs color-disabled">{loadError}</p>}
                    {activeSections.map((section) => {
                        const products = getProductsForSection(section.id);
                        const productRows = chunkProducts(products);
                        const sectionKey = `${activeVariableId}-${section.id}`;
                        const currentSectionState = getRowState(sectionKey);

                        return (
                            <section
                                className="tta-section-group"
                                key={section.id}
                            >
                                {productRows.map((rowProducts, rowIndex) => {
                                    return (
                                        <div
                                            className={`tta-section-wrapper${showSectionLabels ? "" : " without-label"}`}
                                            key={`${sectionKey}-${rowIndex}`}
                                        >
                                            {showSectionLabels && <SectionLabel label={section.label} />}
                                            <div className="tta-section-content">
                                                {rowIndex === 0 && <div className="tta-section-toolbar">
                                                    <Button
                                                        size="small"
                                                        className="custom-btn custom-btn-outlined-2 color-blue bold-content"
                                                        variant="outlined"
                                                        startIcon={
                                                            <img
                                                                src={filterAcrossProducts}
                                                                alt="Filter across products"
                                                            />
                                                        }
                                                    >
                                                        Filter across products
                                                        <Switch
                                                            size="small"
                                                            checked={currentSectionState.filterAcrossEnabled}
                                                            onChange={() => handleFilterAcrossToggle(sectionKey)}
                                                        />
                                                    </Button>
                                                </div>}
                                                <div className="tta-grid">
                                                    {rowProducts.map((product) => (
                                                        <ProductCardLayout
                                                            key={`${activeVariableId}-${section.id}-${rowIndex}-${product.id}`}
                                                            product={product}
                                                            tabState={currentSectionState}
                                                            filterStatus={
                                                                currentSectionState.filterAcrossEnabled
                                                                    ? getFilterStatus(
                                                                        product,
                                                                        currentSectionState.globalAppliedFilters,
                                                                        currentSectionState.filterSourceProductId
                                                                    )
                                                                    : null
                                                            }
                                                            onCardFilterChange={(productId, nextFilters) => handleCardFilterChange(sectionKey, products, productId, nextFilters)}
                                                            onDisplayLabelChange={(productId, nextId) => {
                                                                updateRowState(sectionKey, {
                                                                    selectedDisplayLabels: { ...currentSectionState.selectedDisplayLabels, [productId]: nextId },
                                                                });
                                                                const nextOption = product.displayLabels.find((option) => option.id === nextId);
                                                                if (nextOption) {
                                                                    dispatch(updateThemeSelection({
                                                                        productId,
                                                                        selections: themeSelectionsFromOption(product, nextOption),
                                                                    }));
                                                                }
                                                            }}
                                                            onChartModeChange={(productId, nextMode) => {
                                                                updateRowState(sectionKey, {
                                                                    chartModes: { ...currentSectionState.chartModes, [productId]: nextMode },
                                                                });
                                                            }}
                                                            onViewMore={(currentProduct, selectedDisplay) => setModalData({ product: currentProduct, selectedDisplay })}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </section>
                        );
                    })}
                </main>
                {variableTabs.length > 0 && (
                    <nav
                        className="tta-variable-tabs"
                        aria-label="Text theme variables"
                    >
                        {variableTabs.map((variable) => (
                            <button
                                type="button"
                                className={variable.id === activeVariableId ? "active" : ""}
                                key={variable.id}
                                onClick={() => setActiveVariableId(variable.id)}
                            >
                                <span>{variable.label}</span>
                            </button>
                        ))}
                    </nav>
                )}
            </div>
            <ViewMoreModal
                isOpen={Boolean(modalData)}
                onClose={() => setModalData(null)}
                title={modalData ? `${modalData.selectedDisplay.label} - ${modalData.selectedDisplay.openEndedQuestion}` : "Top Verbatim Responses"}
                expandedResponses={modalData?.selectedDisplay?.verbatimResponsesExpanded || modalData?.product.verbatimResponsesExpanded}
            />
        </>
    );
}

TextThemeAnalysis.propTypes = {
    headerTitle: PropTypes.string,
};

export default TextThemeAnalysis;
